// When a patient names a window ("next week", "November"), suggestions
// should start there. When they do not, the caller keeps soonest-available.
// A part of a month ("mid December", "second week of November", "Dec 15")
// moves the start within that month. The end stays the end of the month.

export type RequestedWindow = {
  start: Date;
  end: Date;
  label: string;
};

const WEEKDAYS = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

const MONTHS: Array<{ names: string[]; index: number }> = [
  { names: ['january', 'jan'], index: 0 },
  { names: ['february', 'feb'], index: 1 },
  { names: ['march'], index: 2 },
  { names: ['april', 'apr'], index: 3 },
  { names: ['june', 'jun'], index: 5 },
  { names: ['july', 'jul'], index: 6 },
  { names: ['august', 'aug'], index: 7 },
  { names: ['september', 'sept', 'sep'], index: 8 },
  { names: ['october', 'oct'], index: 9 },
  { names: ['november', 'novem', 'nov'], index: 10 },
  { names: ['december', 'decem', 'dec'], index: 11 },
  // "may" is too common as a verb — only with a clear month cue.
  { names: [], index: 4 },
];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const addDays = (d: Date, n: number) => {
  const next = startOfDay(d);
  next.setDate(next.getDate() + n);
  return next;
};

const lastDayOfMonth = (year: number, monthIndex: number) =>
  new Date(year, monthIndex + 1, 0);

const nextMonday = (from: Date) => {
  const day = from.getDay();
  const delta = day === 0 ? 1 : 8 - day;
  return addDays(from, delta);
};

const comingWeekday = (from: Date, weekday: number) => {
  const delta = (weekday - from.getDay() + 7) % 7;
  return addDays(from, delta === 0 ? 7 : delta);
};

const monthFromText = (text: string): number | null => {
  if (/\bin may\b/.test(text) || /\bmay \d{4}\b/.test(text)) return 4;
  for (const month of MONTHS) {
    if (month.names.some((name) => (
      name.length <= 4
        ? new RegExp(`\\b${name}\\b`).test(text)
        : text.includes(name)
    ))) return month.index;
  }
  return null;
};

const yearFromText = (text: string, now: Date, monthIndex: number | null): number => {
  const yearMatch = text.match(/\b(20\d{2})\b/);
  if (yearMatch) return Number(yearMatch[1]);
  if (monthIndex === null) return now.getFullYear();
  const thisYear = now.getFullYear();
  const endOfThatMonth = lastDayOfMonth(thisYear, monthIndex);
  return endOfThatMonth < startOfDay(now) ? thisYear + 1 : thisYear;
};

// The words that name one month, as a regex fragment. Long names allow a
// typo after the stem ("novemmber"), as monthFromText does.
const monthWords = (monthIndex: number): string => {
  const names = monthIndex === 4 ? ['may'] : (MONTHS.find((m) => m.index === monthIndex)?.names || []);
  return `(?:${names.map((name) => (name.length <= 4 ? `${name}\\b` : `${name}\\w*`)).join('|')})`;
};

const DAY = '([1-9]|[12]\\d|3[01])';
const ORDINAL = '(?:st|nd|rd|th)';
const WEEKS: Record<string, number> = {
  first: 1, '1st': 1, second: 8, '2nd': 8, third: 15, '3rd': 15, fourth: 22, '4th': 22,
};

type Part = { day: number | 'last-week'; label: string };

// A part of the month named by `month` (a regex fragment), or null. Each
// phrase must sit next to the month words, so "late afternoon in December"
// is still just December.
const partOfMonth = (text: string, month: string, strictDay = false): Part | null => {
  const sep = '[\\s-]+(?:the\\s+)?(?:month\\s+of\\s+)?';

  const after = text.match(new RegExp(`\\bafter\\s+(?:the\\s+)?${DAY}${ORDINAL}?(?:\\s+of)?\\s+${month}`))
    || text.match(new RegExp(`\\b${month}\\.?,?\\s+after\\s+(?:the\\s+)?${DAY}${ORDINAL}?\\b`))
    || text.match(new RegExp(`\\bafter\\s+${month}\\.?\\s+${DAY}${ORDINAL}?\\b`));
  if (after) return { day: Number(after[1]) + 1, label: `after the ${after[1]}` };

  const week = text.match(new RegExp(`\\b(first|1st|second|2nd|third|3rd|fourth|4th|last|final)\\s+week\\s+(?:of|in)${sep}${month}`));
  if (week) {
    const which = week[1];
    if (which === 'last' || which === 'final') return { day: 'last-week', label: 'last week of' };
    return { day: WEEKS[which], label: `${which} week of` };
  }

  if (new RegExp(`\\b(?:early|beginning\\s+of|start\\s+of|first\\s+part\\s+of)${sep}${month}`).test(text)) {
    return { day: 1, label: 'early' };
  }
  if (new RegExp(`\\b(?:mid|middle\\s+of|the\\s+middle\\s+of)${sep.replace('[\\s-]+', '[\\s-]*')}${month}`).test(text)) {
    return { day: 15, label: 'mid' };
  }
  if (new RegExp(`\\b(?:late|end\\s+of|the\\s+end\\s+of|last\\s+part\\s+of)${sep}${month}`).test(text)) {
    return { day: 'last-week', label: 'late' };
  }

  // With no month named, "in 1 month" is not the 1st: ask for "1st" or "of".
  const dayFirst = text.match(new RegExp(strictDay
    ? `\\b${DAY}(?:${ORDINAL}(?:\\s+of)?|\\s+of)\\s+${month}`
    : `\\b${DAY}${ORDINAL}?(?:\\s+of)?\\s+${month}`));
  if (dayFirst) return { day: Number(dayFirst[1]), label: `the ${dayFirst[1]}` };
  if (strictDay) return null;
  // A digit after the day is a time ("Dec 15 10am", "Dec 15 2:30") — unless
  // it is a four-digit year ("Nov 15th 2027"), which keeps the day.
  const monthFirst = text.match(new RegExp(`\\b${month}\\.?,?\\s+(?:the\\s+)?${DAY}${ORDINAL}?\\b(?!\\s*(?::|am\\b|pm\\b|a\\.m|p\\.m|\\d(?!\\d{3}\\b)))`));
  if (monthFirst) return { day: Number(monthFirst[1]), label: `the ${monthFirst[1]}` };

  return null;
};

// Where `part` starts in that month, or null for a day the month lacks.
const partStart = (part: Part, year: number, monthIndex: number): Date | null => {
  const last = lastDayOfMonth(year, monthIndex).getDate();
  if (part.day === 'last-week') return new Date(year, monthIndex, last - 6);
  if (part.day < 1 || part.day > last) return null;
  return new Date(year, monthIndex, part.day);
};

const monthWindow = (year: number, monthIndex: number, today: Date, part: Part | null, label: string): RequestedWindow => {
  const first = new Date(year, monthIndex, 1);
  const start = (part && partStart(part, year, monthIndex)) || first;
  const end = lastDayOfMonth(year, monthIndex);
  const clipped = start < today ? today : start;
  return { start: clipped, end, label: part ? `${part.label} ${label}` : label };
};

/**
 * A calendar window the patient named, or null when they were general.
 * `now` is injectable so tests do not depend on the clock.
 */
export function requestedAppointmentWindow(
  message: string | null | undefined,
  now: Date = new Date(),
): RequestedWindow | null {
  const text = String(message || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!text) return null;
  const today = startOfDay(now);

  if (/\btoday\b/.test(text)) {
    return { start: today, end: today, label: 'today' };
  }
  if (/\bday after tomorrow\b/.test(text) || /\bday after\b/.test(text)) {
    const start = addDays(today, 2);
    return { start, end: start, label: 'day after tomorrow' };
  }
  if (/\btomorrow\b/.test(text)) {
    const start = addDays(today, 1);
    return { start, end: start, label: 'tomorrow' };
  }
  if (/\bnext week\b/.test(text)) {
    const start = nextMonday(today);
    const end = addDays(start, 4);
    return { start, end, label: 'next week' };
  }
  if (/\bthis week\b/.test(text)) {
    const fridayDelta = (5 - today.getDay() + 7) % 7;
    const end = addDays(today, fridayDelta);
    return { start: addDays(today, 1), end, label: 'this week' };
  }
  if (/\bnext month\b/.test(text)) {
    const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const part = partOfMonth(text, '(?:next\\s+month)');
    return monthWindow(start.getFullYear(), start.getMonth(), today, part, 'next month');
  }

  const monthIndex = monthFromText(text);
  if (monthIndex !== null) {
    const year = yearFromText(text, today, monthIndex);
    const part = partOfMonth(text, monthWords(monthIndex));
    const label = new Date(year, monthIndex, 1).toLocaleString('en-US', { month: 'long' });
    return monthWindow(year, monthIndex, today, part, label);
  }

  // "12/15", or "12/15/2026". Not a fraction: "1/2 tablet" is a dose.
  const numeric = text.match(/\b(1[0-2]|0?[1-9])\/(3[01]|[12]\d|0?[1-9])(?:\/(\d{4}|\d{2}))?\b(?!\s*(?:tab|pill|cap|dose|mg|ml|cup|hour|hr|day|week|teaspoon|tsp|tbsp|of a\b))/);
  if (numeric && !['1/2', '1/3', '1/4', '2/3', '3/4'].includes(`${Number(numeric[1])}/${Number(numeric[2])}`)) {
    const monthAt = Number(numeric[1]) - 1;
    const day = Number(numeric[2]);
    let year = numeric[3] ? Number(numeric[3].length === 2 ? `20${numeric[3]}` : numeric[3]) : today.getFullYear();
    if (!numeric[3] && new Date(year, monthAt, day) < today) year += 1;
    if (day <= lastDayOfMonth(year, monthAt).getDate()) {
      const label = new Date(year, monthAt, 1).toLocaleString('en-US', { month: 'long' });
      return monthWindow(year, monthAt, today, { day, label: `the ${day}` }, label);
    }
  }

  // "middle of the month" with no month named: this month, or next month
  // once this one is past that point.
  const generic = partOfMonth(text, '(?:(?:this|the)\\s+)?month\\b', true);
  if (generic) {
    const here = partStart(generic, today.getFullYear(), today.getMonth());
    if (here && here >= today) {
      return monthWindow(today.getFullYear(), today.getMonth(), today, generic, 'this month');
    }
    const next = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return monthWindow(next.getFullYear(), next.getMonth(), today, generic, 'next month');
  }

  for (let weekday = 0; weekday < WEEKDAYS.length; weekday += 1) {
    const name = WEEKDAYS[weekday];
    if (new RegExp(`\\b(next )?${name}s?\\b`).test(text)) {
      const start = comingWeekday(today, weekday);
      return { start, end: start, label: name };
    }
  }

  return null;
}

export function windowDayCount(window: RequestedWindow): number {
  const ms = startOfDay(window.end).getTime() - startOfDay(window.start).getTime();
  return Math.max(1, Math.round(ms / 86400000) + 1);
}
